/**
 * /drill/fret-intervals — Fretboard Intervals & Octaves (rung 1).
 *
 * Shape-family redesign (PR-B): three shape-family nodes replace the old
 * whole-neck behaviour. The setup screen lets the user pick a family and
 * optionally select sub-intervals. "Mixed" interleaves standard + G–B + octave shapes.
 *
 * G–B contextual interstitial: shown in Listen mode when the G–B offset family
 * is selected and the node has never been drilled (needs-listen status).
 */

import { useState, useMemo, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import {
  expandNode,
  MVP_NODES,
  EXERCISE_SKILL_NODES,
  type Item,
} from "@music-app/learning-system";
import { useCardsStore } from "@music-app/persistence";
import { ModeSwitcherBar, MODE_COLOR, type ExerciseMode } from "../components/ModeSwitcherBar.js";
import { GuitarDrillCore, GuitarNodeStats } from "./FretboardExercise.js";
import { PageHeader } from "../components/PageHeader.js";
import { buttonClasses } from "../components/Button.js";
import { ExerciseLessonLink } from "../components/ExerciseLessonLink.js";

// ─── Item pools ───────────────────────────────────────────────────────────────

const NODE_IDS = EXERCISE_SKILL_NODES["fret-intervals"];

function nodeItems(id: string): Item[] {
  const node = MVP_NODES.find((n) => n.id === id);
  return node ? expandNode(node) : [];
}

const OCTAVE_ITEMS      = nodeItems("fret-octave-shapes");
const WIDE_OCTAVE_ITEMS = nodeItems("fret-wide-octave-shapes");
const SKIP1_ITEMS       = nodeItems("fret-skip1-intervals");
const STANDARD_ITEMS    = nodeItems("fret-intervals-standard");
const GB_ITEMS          = nodeItems("fret-intervals-gb-offset");

// All items across all nodes (for the session pool)
const ALL_ITEMS = NODE_IDS.flatMap((id) => nodeItems(id));

const INTERVAL_STAT_ROWS = [
  { nodeId: "fret-intervals-standard",  label: "Standard shapes" },
  { nodeId: "fret-intervals-gb-offset", label: "G–B offset" },
  { nodeId: "fret-octave-shapes",       label: "Octave shapes" },
  { nodeId: "fret-wide-octave-shapes",  label: "Wide octaves" },
  { nodeId: "fret-skip1-intervals",     label: "Skip-1 intervals" },
] as const;

// ─── Types ────────────────────────────────────────────────────────────────────

type ShapeFamily = "standard" | "gb-offset" | "octave" | "wide-octave" | "skip1-interval" | "mixed";

interface IntervalInfo {
  id: string;
  symbol: string;
  label: string;
}

function intervalLabel(symbol: string): string {
  const map: Record<string, string> = {
    P5: "P5", P4: "P4", M3: "M3", m3: "m3", M6: "M6", m6: "m6", M2: "M2",
  };
  return map[symbol] ?? symbol;
}

const STANDARD_INTERVALS: IntervalInfo[] = STANDARD_ITEMS.map((item) => ({
  id: item.id,
  symbol: item.params["interval"] as string,
  label: intervalLabel(item.params["interval"] as string),
}));

const GB_INTERVALS: IntervalInfo[] = GB_ITEMS.map((item) => ({
  id: item.id,
  symbol: item.params["interval"] as string,
  label: intervalLabel(item.params["interval"] as string),
}));

const OCTAVE_SHAPE_LABELS: Record<string, string> = {
  "oct-ed": "E–D (6–4)",
  "oct-ag": "A–G (5–3)",
  "oct-db": "D–B (4–2)",
  "oct-ge": "G–e (3–1)",
};

const WIDE_OCTAVE_SHAPE_LABELS: Record<string, string> = {
  "wide-eg": "E–G (6–3)",
  "wide-ab": "A–B (5–2)",
  "wide-de": "D–e (4–1)",
};

const SKIP1_INTERVAL_LABELS: Record<string, string> = {
  "skip1-m7-ed": "m7 (6–4)",
  "skip1-m7-ag": "m7 (5–3)",
  "skip1-m6-db": "M6 (4–2)",
  "skip1-m6-ge": "M6 (3–1)",
};
const OCTAVE_INTERVALS: IntervalInfo[] = OCTAVE_ITEMS.map((item) => ({
  id: item.id,
  symbol: item.params["octaveShapeSymbol"] as string,
  label: OCTAVE_SHAPE_LABELS[item.params["octaveShapeSymbol"] as string] ?? item.id,
}));

const WIDE_OCTAVE_INTERVALS: IntervalInfo[] = WIDE_OCTAVE_ITEMS.map((item) => ({
  id: item.id,
  symbol: item.params["wideOctaveShapeSymbol"] as string,
  label: WIDE_OCTAVE_SHAPE_LABELS[item.params["wideOctaveShapeSymbol"] as string] ?? item.id,
}));

const SKIP1_INTERVALS: IntervalInfo[] = SKIP1_ITEMS.map((item) => ({
  id: item.id,
  symbol: item.params["skip1IntervalSymbol"] as string,
  label: SKIP1_INTERVAL_LABELS[item.params["skip1IntervalSymbol"] as string] ?? item.id,
}));

const SESSION_LENGTHS = [5, 10, 15, 20, 30];
const DEFAULT_SESSION_LENGTH = 10;

// ─── Page ─────────────────────────────────────────────────────────────────────

export function FretIntervalsExercisePage(): ReactNode {
  const [searchParams] = useSearchParams();
  const rawMode = searchParams.get("mode") as ExerciseMode | null;
  const mode: Exclude<ExerciseMode, "challenge"> =
    rawMode === "listen" || rawMode === "review" ? rawMode : "practice";
  const from    = searchParams.get("from") ?? undefined;
  const coached = searchParams.get("coached") === "true";
  const nodeId  = searchParams.get("nodeId") ?? undefined;

  const [family, setFamily] = useState<ShapeFamily>(() => {
    switch (nodeId) {
      case "fret-intervals-gb-offset": return "gb-offset";
      case "fret-octave-shapes": return "octave";
      case "fret-wide-octave-shapes": return "wide-octave";
      case "fret-skip1-intervals": return "skip1-interval";
      default: return "standard";
    }
  });
  const [sessionLength, setSessionLength] = useState(DEFAULT_SESSION_LENGTH);

  // Per-family interval selections
  const [standardIds, setStandardIds] = useState<ReadonlySet<string>>(
    () => new Set(STANDARD_ITEMS.map((i) => i.id)),
  );
  const [gbIds, setGbIds] = useState<ReadonlySet<string>>(
    () => new Set(GB_ITEMS.map((i) => i.id)),
  );
  const [octaveIds, setOctaveIds] = useState<ReadonlySet<string>>(
    () => new Set(OCTAVE_ITEMS.map((i) => i.id)),
  );
  const [wideOctaveIds, setWideOctaveIds] = useState<ReadonlySet<string>>(
    () => new Set(WIDE_OCTAVE_ITEMS.map((i) => i.id)),
  );
  const [skip1Ids, setSkip1Ids] = useState<ReadonlySet<string>>(
    () => new Set(SKIP1_ITEMS.map((i) => i.id)),
  );

  // G–B needs-listen detection: true when no gb-offset item has ever been drilled.
  const cards = useCardsStore((s) => s.cards);
  const gbNeedsListen = useMemo(
    () => GB_ITEMS.every((item) => !cards[item.id] || cards[item.id]!.reps === 0),
    [cards],
  );

  const makeToggle = (
    setter: (fn: (prev: ReadonlySet<string>) => ReadonlySet<string>) => void,
  ) =>
    (id: string) =>
      setter((prev) => {
        const next = new Set(prev);
        if (next.has(id)) {
          if (next.size <= 1) return prev;
          next.delete(id);
        } else {
          next.add(id);
        }
        return next;
      });

  const toggleStandard   = makeToggle(setStandardIds);
  const toggleGb         = makeToggle(setGbIds);
  const toggleOctave     = makeToggle(setOctaveIds);
  const toggleWideOctave = makeToggle(setWideOctaveIds);
  const toggleSkip1      = makeToggle(setSkip1Ids);

  const nodeItems = useMemo((): Item[] => {
    switch (family) {
      case "standard":
        return STANDARD_ITEMS.filter((i) => standardIds.has(i.id));
      case "gb-offset":
        return GB_ITEMS.filter((i) => gbIds.has(i.id));
      case "octave":
        return OCTAVE_ITEMS.filter((i) => octaveIds.has(i.id));
      case "wide-octave":
        return WIDE_OCTAVE_ITEMS.filter((i) => wideOctaveIds.has(i.id));
      case "skip1-interval":
        return SKIP1_ITEMS.filter((i) => skip1Ids.has(i.id));
      case "mixed": {
        const stdFiltered  = STANDARD_ITEMS.filter((i) => standardIds.has(i.id));
        const gbFiltered   = GB_ITEMS.filter((i) => gbIds.has(i.id));
        const octFiltered  = OCTAVE_ITEMS.filter((i) => octaveIds.has(i.id));
        const wideFiltered = WIDE_OCTAVE_ITEMS.filter((i) => wideOctaveIds.has(i.id));
        const skip1Filtered = SKIP1_ITEMS.filter((i) => skip1Ids.has(i.id));
        return [...stdFiltered, ...gbFiltered, ...octFiltered, ...wideFiltered, ...skip1Filtered];
      }
    }
  }, [family, standardIds, gbIds, octaveIds, wideOctaveIds, skip1Ids]);

  // Show the G–B contextual interstitial when in Listen mode with G–B items
  // and the user hasn't drilled this node yet.
  const showGbInterstitial =
    mode === "listen" &&
    (family === "gb-offset" || family === "mixed") &&
    gbNeedsListen;

  return (
    <GuitarDrillCore
      skillId="fret-intervals"
      nodeItems={nodeItems.length > 0 ? nodeItems : ALL_ITEMS}
      exercisePath="/guitar/fret-intervals"
      drillTitle="Fretboard Intervals"
      mode={mode}
      nodeId={nodeId}
      from={from}
      coached={coached}
      sessionLength={sessionLength}
      showGbInterstitial={showGbInterstitial}
      renderSetupScreen={(onStart, ready) => (
        <SetupScreen
          mode={mode}
          from={from}
          family={family}
          onFamily={setFamily}
          standardIntervals={STANDARD_INTERVALS}
          standardIds={standardIds}
          onToggleStandard={toggleStandard}
          gbIntervals={GB_INTERVALS}
          gbIds={gbIds}
          onToggleGb={toggleGb}
          octaveIntervals={OCTAVE_INTERVALS}
          octaveIds={octaveIds}
          onToggleOctave={toggleOctave}
          wideOctaveIntervals={WIDE_OCTAVE_INTERVALS}
          wideOctaveIds={wideOctaveIds}
          onToggleWideOctave={toggleWideOctave}
          skip1Intervals={SKIP1_INTERVALS}
          skip1Ids={skip1Ids}
          onToggleSkip1={toggleSkip1}
          sessionLength={sessionLength}
          onSessionLength={setSessionLength}
          ready={ready}
          onStart={onStart}
        />
      )}
    />
  );
}

// ─── Setup screen ─────────────────────────────────────────────────────────────

interface SetupProps {
  mode: Exclude<ExerciseMode, "challenge">;
  from?: string;
  family: ShapeFamily;
  onFamily: (f: ShapeFamily) => void;
  standardIntervals: IntervalInfo[];
  standardIds: ReadonlySet<string>;
  onToggleStandard: (id: string) => void;
  gbIntervals: IntervalInfo[];
  gbIds: ReadonlySet<string>;
  onToggleGb: (id: string) => void;
  octaveIntervals: IntervalInfo[];
  octaveIds: ReadonlySet<string>;
  onToggleOctave: (id: string) => void;
  wideOctaveIntervals: IntervalInfo[];
  wideOctaveIds: ReadonlySet<string>;
  onToggleWideOctave: (id: string) => void;
  skip1Intervals: IntervalInfo[];
  skip1Ids: ReadonlySet<string>;
  onToggleSkip1: (id: string) => void;
  sessionLength: number;
  onSessionLength: (n: number) => void;
  ready: boolean;
  onStart: () => void;
}

const FAMILIES: Array<{ id: ShapeFamily; label: string; description: string }> = [
  { id: "standard",      label: "Standard shapes",   description: "All four P4 pairs — same shape every time" },
  { id: "gb-offset",     label: "G–B offset",        description: "The G–B pair — every shape +1 fret" },
  { id: "octave",        label: "Octave shapes",     description: "Four skip-1-string grips (+2/+3 frets)" },
  { id: "wide-octave",   label: "Wide octaves",      description: "Skip-2-string grips — upper note 2–3 frets back" },
  { id: "skip1-interval",label: "Skip-1 intervals",  description: "m7 and M6 on string-skip pairs — same fret as root" },
  { id: "mixed",         label: "Mixed",             description: "All families interleaved" },
];

function IntervalChips({
  intervals,
  selectedIds,
  onToggle,
  modeColor,
}: {
  intervals: IntervalInfo[];
  selectedIds: ReadonlySet<string>;
  onToggle: (id: string) => void;
  modeColor: string;
}): ReactNode {
  return (
    <div className="flex flex-wrap gap-2">
      {intervals.map((iv) => {
        const active = selectedIds.has(iv.id);
        return (
          <button
            key={iv.id}
            type="button"
            onClick={() => onToggle(iv.id)}
            aria-pressed={active}
            className="px-3 py-1.5 font-mono text-xs border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ink/30"
            style={
              active
                ? { borderColor: modeColor, backgroundColor: modeColor, color: "#FAF6EE" }
                : { borderColor: "rgba(26,24,19,0.2)", backgroundColor: "#FAF6EE", color: "#1A1813" }
            }
          >
            {iv.label}
          </button>
        );
      })}
    </div>
  );
}

function SetupScreen({
  mode,
  from,
  family,
  onFamily,
  standardIntervals,
  standardIds,
  onToggleStandard,
  gbIntervals,
  gbIds,
  onToggleGb,
  octaveIntervals,
  octaveIds,
  onToggleOctave,
  wideOctaveIntervals,
  wideOctaveIds,
  onToggleWideOctave,
  skip1Intervals,
  skip1Ids,
  onToggleSkip1,
  sessionLength,
  onSessionLength,
  ready,
  onStart,
}: SetupProps): ReactNode {
  const modeColor = MODE_COLOR[mode];
  const [showCustomLength, setShowCustomLength] = useState(false);
  const backLink =
    from === "today" ? { label: "← Today", href: "/" } :
    from === "learn" ? { label: "← Learn", href: "/learn" } :
    { label: "← Guitar Hub", href: "/guitar" };

  return (
    <div className="max-w-2xl mx-auto py-9 px-8 pb-16">
      <PageHeader
        back={backLink}
        title="Fretboard Intervals"
        subhead="Hear an interval above a root — place its shape on the string pair."
      />

      <ModeSwitcherBar current={mode} exercisePath="/guitar/fret-intervals" />
      <ExerciseLessonLink nodeIds={[...NODE_IDS]} />

      <GuitarNodeStats rows={INTERVAL_STAT_ROWS} />

      {/* Shape-family selector */}
      <section className="mb-8">
        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded mb-3">
          Shape family
        </p>
        <div className="flex flex-col gap-2">
          {FAMILIES.map((f) => {
            const active = family === f.id;
            return (
              <button
                key={f.id}
                type="button"
                onClick={() => onFamily(f.id)}
                aria-pressed={active}
                className="flex items-center gap-3 px-4 py-3 border text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ink/30"
                style={
                  active
                    ? { borderColor: modeColor, backgroundColor: modeColor + "1A" }
                    : { borderColor: "rgba(26,24,19,0.2)", backgroundColor: "#FAF6EE" }
                }
              >
                <span
                  className="w-3 h-3 rounded-full border-2 shrink-0"
                  style={
                    active
                      ? { borderColor: modeColor, backgroundColor: modeColor }
                      : { borderColor: "rgba(26,24,19,0.3)", backgroundColor: "transparent" }
                  }
                  aria-hidden
                />
                <span>
                  <span className="font-mono text-xs font-medium text-ink block">{f.label}</span>
                  <span className="font-serif italic text-xs text-inkSoft">{f.description}</span>
                </span>
              </button>
            );
          })}
        </div>
      </section>

      {/* Per-family interval/shape selector */}
      {(family === "standard" || family === "mixed") && (
        <section className="mb-6">
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded mb-3">
            Intervals — standard shapes
          </p>
          <IntervalChips
            intervals={standardIntervals}
            selectedIds={standardIds}
            onToggle={onToggleStandard}
            modeColor={modeColor}
          />
        </section>
      )}

      {(family === "gb-offset" || family === "mixed") && (
        <section className="mb-6">
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded mb-3">
            Intervals — G–B offset
          </p>
          <IntervalChips
            intervals={gbIntervals}
            selectedIds={gbIds}
            onToggle={onToggleGb}
            modeColor={modeColor}
          />
        </section>
      )}

      {(family === "octave" || family === "mixed") && (
        <section className="mb-6">
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded mb-3">
            Octave shapes
          </p>
          <IntervalChips
            intervals={octaveIntervals}
            selectedIds={octaveIds}
            onToggle={onToggleOctave}
            modeColor={modeColor}
          />
        </section>
      )}

      {(family === "wide-octave" || family === "mixed") && (
        <section className="mb-6">
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded mb-3">
            Wide octave shapes
          </p>
          <IntervalChips
            intervals={wideOctaveIntervals}
            selectedIds={wideOctaveIds}
            onToggle={onToggleWideOctave}
            modeColor={modeColor}
          />
        </section>
      )}

      {(family === "skip1-interval" || family === "mixed") && (
        <section className="mb-6">
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded mb-3">
            Skip-1 intervals
          </p>
          <IntervalChips
            intervals={skip1Intervals}
            selectedIds={skip1Ids}
            onToggle={onToggleSkip1}
            modeColor={modeColor}
          />
        </section>
      )}

      <section className="mb-8">
        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded mb-3">
          Exercises per session
        </p>
        <div className="flex items-center gap-2 flex-wrap">
          {SESSION_LENGTHS.map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => { onSessionLength(n); setShowCustomLength(false); }}
              aria-pressed={!showCustomLength && sessionLength === n}
              className="px-4 py-1.5 font-mono text-xs border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ink/30"
              style={
                !showCustomLength && sessionLength === n
                  ? { borderColor: modeColor, backgroundColor: modeColor, color: "#FAF6EE" }
                  : { borderColor: "rgba(26,24,19,0.2)", backgroundColor: "#FAF6EE", color: "#1A1813" }
              }
            >
              {n}
            </button>
          ))}
          <button
            type="button"
            onClick={() => setShowCustomLength(true)}
            aria-pressed={showCustomLength}
            className="px-4 py-1.5 font-mono text-xs border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ink/30"
            style={showCustomLength ? { borderColor: modeColor, backgroundColor: modeColor, color: "#FAF6EE" } : { borderColor: "rgba(26,24,19,0.2)", backgroundColor: "#FAF6EE", color: "#1A1813" }}
          >
            Other…
          </button>
          {showCustomLength && (
            <input
              type="number"
              min={1}
              max={50}
              value={sessionLength}
              onChange={(e) => {
                const v = parseInt(e.target.value, 10);
                if (!isNaN(v) && v >= 1 && v <= 50) onSessionLength(v);
              }}
              className="w-16 px-3 py-1.5 font-mono text-xs border border-ink/20 bg-paper text-ink text-center focus:outline-none focus:border-ink"
              aria-label="Custom number of exercises"
              autoFocus
            />
          )}
        </div>
      </section>

      <button
        type="button"
        onClick={onStart}
        disabled={!ready}
        aria-busy={!ready}
        className={`mt-8 ${buttonClasses("primary")}`}
        style={{ backgroundColor: MODE_COLOR[mode] }}
      >
        {ready ? "Start →" : "Loading sounds…"}
      </button>
      <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded mt-4">
        {ready ? "Audio starts on tap." : "Preparing the instrument…"}
      </p>
    </div>
  );
}
